/**
 * Sliding-window byte rate, for the measured-bitrate readout.
 *
 * A plain "bytes since last tick" counter reads wildly differently depending on
 * when the UI happens to sample it. Bytes are bucketed into fixed slots instead and
 * a whole window is summed, which gives a number stable enough to compare against
 * the bitrate the sender announced. The bucket currently being filled is left out
 * of the sum — including a partial bucket is exactly what makes naive rate meters
 * flicker.
 */
export class RateMeter {
  private readonly buckets: number[];
  private readonly windowBuckets: number;
  private readonly bucketMs: number;
  private lastBucket = -1;

  constructor(windowMs = 3000, bucketMs = 250) {
    this.bucketMs = Math.max(1, bucketMs);
    this.windowBuckets = Math.max(1, Math.ceil(windowMs / this.bucketMs));
    this.buckets = new Array<number>(this.windowBuckets + 1).fill(0);
  }

  add(bytes: number, atMs: number): void {
    const bucket = Math.floor(atMs / this.bucketMs);
    this.rotate(bucket);
    const index = bucket % this.buckets.length;
    this.buckets[index] = (this.buckets[index] ?? 0) + bytes;
  }

  /** Bits per second over the completed window. */
  bitsPerSecond(atMs: number): number {
    const bucket = Math.floor(atMs / this.bucketMs);
    this.rotate(bucket);

    const current = bucket % this.buckets.length;
    let total = 0;
    for (let i = 0; i < this.buckets.length; i++) {
      if (i !== current) total += this.buckets[i] ?? 0;
    }
    return (total * 8 * 1000) / (this.windowBuckets * this.bucketMs);
  }

  reset(): void {
    this.buckets.fill(0);
    this.lastBucket = -1;
  }

  private rotate(bucket: number): void {
    if (this.lastBucket < 0) {
      this.lastBucket = bucket;
      return;
    }
    if (bucket <= this.lastBucket) return;

    const steps = Math.min(bucket - this.lastBucket, this.buckets.length);
    for (let i = 1; i <= steps; i++) {
      this.buckets[(this.lastBucket + i) % this.buckets.length] = 0;
    }
    this.lastBucket = bucket;
  }
}
